/**
 * Centralized runtime for ephemeral coordination state.
 * Holds dispatch/execution marks and delete de-dupe keys without causing rerenders.
 * Kept intentionally minimal to avoid introducing new features.
 */

export interface RuntimeMarks {
	dispatched: Set<string>;
	executing: Set<string>;
	lastDispatchTime: Map<string, number>;
}

export interface Runtime {
	marks: RuntimeMarks;
	/** Tracks houseId:documentId keys for delete de-duplication across calls */
	activeDeleteKeys: Set<string>;
	/** Active request controllers keyed by the stable UI task id. */
	abortControllers: Map<string, AbortController>;

	/** Returns true if the task can be dispatched at `now` respecting a small debounce window. */
	canDispatch: (id: string, now: number, debounceMs?: number) => boolean;
	/** Marks task as dispatched and records the last dispatch time. */
	markDispatched: (id: string, now: number) => void;
	/** Marks task as executing. */
	markExecuting: (id: string) => void;
	/** Releases executing mark. */
	releaseExecuting: (id: string) => void;
	/** Releases dispatched mark. */
	releaseDispatched: (id: string) => void;
	/** Registers the request controller before a task is dispatched. */
	registerAbortController: (id: string, controller: AbortController) => void;
	/** Aborts an active request and returns whether a controller was present. */
	abortTask: (id: string) => boolean;
	/** Releases a controller only when it still belongs to the supplied request. */
	releaseAbortController: (id: string, signal: AbortSignal) => void;
	/** Clears all runtime state (used on house change). */
	clear: () => void;
}

/** Factory for a stable runtime instance. */
export function createRuntime(): Runtime {
	const marks: RuntimeMarks = {
		dispatched: new Set<string>(),
		executing: new Set<string>(),
		lastDispatchTime: new Map<string, number>(),
	};

	const activeDeleteKeys = new Set<string>();
	const abortControllers = new Map<string, AbortController>();

	return {
		marks,
		activeDeleteKeys,
		abortControllers,
		canDispatch: (id: string, now: number, debounceMs: number = 50) => {
			const last = marks.lastDispatchTime.get(id) || 0;
			if (now - last < debounceMs) return false;
			if (marks.executing.has(id)) return false;
			return true;
		},
		markDispatched: (id: string, now: number) => {
			marks.lastDispatchTime.set(id, now);
			marks.dispatched.add(id);
		},
		markExecuting: (id: string) => {
			marks.executing.add(id);
		},
		releaseExecuting: (id: string) => {
			marks.executing.delete(id);
		},
		releaseDispatched: (id: string) => {
			marks.dispatched.delete(id);
		},
		registerAbortController: (id: string, controller: AbortController) => {
			abortControllers.set(id, controller);
		},
		abortTask: (id: string) => {
			const controller = abortControllers.get(id);
			if (!controller) return false;
			abortControllers.delete(id);
			controller.abort();
			return true;
		},
		releaseAbortController: (id: string, signal: AbortSignal) => {
			if (abortControllers.get(id)?.signal === signal) {
				abortControllers.delete(id);
			}
		},
		clear: () => {
			abortControllers.forEach((controller) => controller.abort());
			abortControllers.clear();
			marks.dispatched.clear();
			marks.executing.clear();
			marks.lastDispatchTime.clear();
			activeDeleteKeys.clear();
		},
	};
}
